/* eslint "react/no-children-prop": "off" */

'use client';

import z from 'zod';
import { contactSection } from '@stereoagency/sanity-schema';
import {
  Button,
  FormInput,
  ContactSection as UIContactSection
} from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import Turnstile from 'react-turnstile';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { SimpleBlockContent } from '@/components/sanity/client';
import type { InputSchema } from '@/app/(root)/api/contact/route';

type Props = Omit<typeof contactSection.schema.contactSection, '_type'>;

const schema = z.object({
  name: z.string().min(1),
  firstname: z.string().min(1),
  phone: z.string().optional(),
  email: z.string().min(1).email(),
  message: z.string().min(1),
  $turnstile_key: z.string().min(1)
});

export default function ContactSection({ title, description }: Props) {
  const { t } = useTranslation();
  const { register, control, formState, handleSubmit } = useForm<
    z.infer<typeof schema>
  >({
    resolver: zodResolver(schema)
  });
  const { errors, isSubmitting, isSubmitSuccessful } = formState;

  const handleSuccess = async (values: z.infer<typeof schema>) => {
    const url = new URL('/api/contact', window.location.href);
    const body: InputSchema = {
      lastname: values.name,
      firstname: values.firstname,
      tel: values.phone,
      email: values.email,
      message: values.message,
      $turnstile_token: values.$turnstile_key
    };
    await fetch(url.toString(), {
      method: 'POST',
      body: JSON.stringify(body),
      headers: [['Content-Type', 'application/json']]
    });
  };

  return (
    <UIContactSection
      title={title}
      description={
        description ? <SimpleBlockContent value={description} /> : null
      }
      formProps={{
        onSubmit: handleSubmit(handleSuccess)
      }}
      successState={
        isSubmitSuccessful
          ? {
              title: t('contact-form:success-state.title'),
              description: t('contact-form:success-state.description')
            }
          : undefined
      }
    >
      <FormInput
        required
        label={t('contact-form:lastname')}
        error={errors.name?.message}
        {...register('name')}
      />
      <FormInput
        required
        label={t('contact-form:firstname')}
        error={errors.firstname?.message}
        {...register('firstname')}
      />
      <FormInput
        label={t('contact-form:phone')}
        {...register('phone')}
        error={errors.phone?.message}
      />
      <FormInput
        required
        label={t('contact-form:email')}
        error={errors.email?.message}
        {...register('email')}
      />
      <FormInput
        required
        label={t('contact-form:message')}
        error={errors.message?.message}
        {...register('message')}
      />
      <Controller
        name="$turnstile_key"
        control={control}
        render={({ field }) => (
          <span>
            <Turnstile
              sitekey={process.env.TURNSTILE_SITE_KEY ?? ''}
              onVerify={(value) => {
                field.onBlur();
                field.onChange({ target: { name: '$turnstile_key', value } });
              }}
            />
            {!!errors.$turnstile_key?.message && (
              <span className="text-body-small font-semibold font-heading text-red-700">
                {errors.$turnstile_key.message}
              </span>
            )}
          </span>
        )}
      />
      <Button variant="primary" htmlType="submit" disabled={isSubmitting}>
        {t('contact-form:send')}
      </Button>
    </UIContactSection>
  );
}
